/**
 * 1-click trading (#688) UI: the enable/disable control (wallet menu + order ticket), the "on · expires …"
 * indicator, and the one dialog shown while the wallet approves the agent.
 */
import Modal from '../components/Modal';
import { useEnv } from '../config/env';
import { formatExpiry } from './agent';
import { useAgent } from './context';
import Msg from '../i18n/Msg';
import { networkWord } from '../i18n/common';
import { useT } from '../i18n/useT';
import { browseOnlyMessage, useGeo } from '../geo/context';

const BTN = 'rounded-md border border-border px-2 py-1 text-xs font-medium hover:border-accent hover:text-accent disabled:opacity-50';

/** "1-click trading on · expires Oct 24, 14:05" pill. Renders nothing unless on. */
export function AgentIndicator({ className = '' }: { className?: string }) {
  const agent = useAgent();
  const t = useT();
  if (agent.status !== 'on' || !agent.session) return null;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full bg-yes/15 px-2 py-0.5 text-[11px] font-medium text-yes ${className}`}
      data-testid="agent-indicator"
      title={t('agent.indicator_title', { address: agent.session.agentAddress })}
    >
      <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-yes" />
      {t('agent.indicator', { expiry: formatExpiry(agent.session.validUntil) })}
    </span>
  );
}

/**
 * Enable / disable control. `variant="menu"` for the wallet menu (compact), `"ticket"` for the order ticket (with
 * a one-line explanation and the last error / notice).
 */
export function AgentToggle({ variant, onAction }: { variant: 'menu' | 'ticket'; onAction?: () => void }) {
  const agent = useAgent();
  const t = useT();
  const geo = useGeo();
  if (agent.status === 'unavailable') {
    if (variant === 'menu' && agent.unavailableReason === 'region') {
      return (
        <div className="px-3 py-2 text-xs text-muted" data-testid="agent-unavailable">
          {t('geo.agent_unavailable', { reason: browseOnlyMessage(geo.decision) })}
        </div>
      );
    }
    if (variant === 'menu' && agent.unavailableReason === 'ghost') {
      return (
        <div className="px-3 py-2 text-xs text-muted" data-testid="agent-unavailable">
          {t('agent.unavailable_ghost')}
        </div>
      );
    }
    return null;
  }

  const messages =
    variant === 'ticket' && (agent.error || agent.notice) ? (
      <p role="status" className={`text-[11px] ${agent.error ? 'text-danger' : 'text-warn'}`} data-testid="agent-message">
        {agent.error ?? agent.notice}
      </p>
    ) : null;

  if (agent.status === 'on') {
    return (
      <div className={variant === 'menu' ? 'space-y-1 px-3 py-2' : 'space-y-1'} data-testid={`agent-toggle-${variant}`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <AgentIndicator />
          <button
            type="button"
            className={BTN}
            onClick={() => {
              agent.disable();
              onAction?.();
            }}
          >
            {t('agent.disable')}
          </button>
        </div>
        {messages}
      </div>
    );
  }

  const enabling = agent.status === 'enabling';
  return (
    <div className={variant === 'menu' ? 'space-y-1 px-3 py-2' : 'space-y-1'} data-testid={`agent-toggle-${variant}`}>
      <button
        type="button"
        className={variant === 'menu' ? `${BTN} w-full` : BTN}
        disabled={enabling}
        onClick={() => {
          onAction?.();
          void agent.enable();
        }}
      >
        {enabling ? t('agent.approve_in_wallet') : t('agent.enable')}
      </button>
      {variant === 'ticket' && (
        <p className="text-[11px] text-muted">{t('agent.ticket_hint')}</p>
      )}
      {variant === 'menu' && agent.error && <p className="text-[11px] text-danger">{agent.error}</p>}
      {messages}
    </div>
  );
}

/** The dialog while the wallet is asked to approve the agent. Render once (App). */
export function AgentEnablingModal() {
  const agent = useAgent();
  const { env } = useEnv();
  const t = useT();
  const open = agent.status === 'enabling';
  return (
    <Modal open={open} onClose={() => undefined} title={t('agent.enable')}>
      {open && (
        <div className="space-y-2 text-xs text-muted" data-testid="agent-enabling">
          <p className="text-sm text-fg">{t('agent.modal.approve')}</p>
          <p>
            <Msg
              k="agent.modal.what"
              args={{
                msg: (
                  <span className="font-mono" translate="no">
                    ApproveAgent
                  </span>
                ),
                network: networkWord(env.name),
              }}
            />
          </p>
          <p>{t('agent.modal.safety')}</p>
        </div>
      )}
    </Modal>
  );
}
